import React, { useState, useEffect } from 'react';
import Header from '../../components/ui/Header';
import Breadcrumb from '../../components/ui/Breadcrumb';
import FilterPanel from './components/FilterPanel';
import ViolationsTable from './components/ViolationsTable';
import { violationRecordsService, employeesService, violationCategoriesService } from '../../services/supabaseService';

const AllViolations = () => {
  const [violations, setViolations] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredViolations, setFilteredViolations] = useState([]);
  
  // Filter states
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedDepartment, setSelectedDepartment] = useState('all');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [pointRange, setPointRange] = useState({ min: 0, max: 100 });
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'date', direction: 'desc' });

  const breadcrumbItems = [
    { label: 'Home', path: '/' },
    { label: 'All Violations', path: '/all-violations' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    applyFiltersAndSort();
  }, [violations, employees, searchTerm, selectedCategory, selectedDepartment, dateRange, pointRange, sortConfig]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [violationsData, employeesData, categoriesData] = await Promise.all([
        violationRecordsService?.getAll(),
        employeesService?.getAll(),
        violationCategoriesService?.getAll()
      ]);
      setViolations(violationsData || []);
      setEmployees(employeesData || []);
      setCategories(categoriesData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyFiltersAndSort = () => {
    let filtered = violations?.map(violation => {
      const employee = employees?.find(e => e?.id === violation?.employeeId);
      return {
        ...violation,
        employeeName: employee?.name || 'Unknown',
        employeeIdDisplay: employee?.employeeId || 'N/A',
        department: employee?.department || 'N/A',
        avatar: employee?.avatar,
        avatarAlt: employee?.avatarAlt || `${employee?.name} avatar`
      };
    }) || [];

    // Apply search filter
    if (searchTerm) {
      const search = searchTerm?.toLowerCase();
      filtered = filtered?.filter(v => 
        v?.employeeName?.toLowerCase()?.includes(search) ||
        v?.type?.toLowerCase()?.includes(search) ||
        v?.description?.toLowerCase()?.includes(search) ||
        v?.employeeIdDisplay?.toLowerCase()?.includes(search)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'all') {
      filtered = filtered?.filter(v => v?.category === selectedCategory);
    }

    // Apply department filter
    if (selectedDepartment !== 'all') {
      filtered = filtered?.filter(v => v?.department === selectedDepartment);
    }

    // Apply date range filter
    if (dateRange?.start) {
      filtered = filtered?.filter(v => new Date(v?.date) >= new Date(dateRange?.start));
    }
    if (dateRange?.end) {
      filtered = filtered?.filter(v => new Date(v?.date) <= new Date(dateRange?.end));
    }

    // Apply point range filter
    filtered = filtered?.filter(v => 
      v?.points >= pointRange?.min && v?.points <= pointRange?.max
    );

    // Apply sorting
    filtered?.sort((a, b) => {
      let aValue = a?.[sortConfig?.key];
      let bValue = b?.[sortConfig?.key];

      // Handle date sorting
      if (sortConfig?.key === 'date') {
        aValue = new Date(aValue)?.getTime();
        bValue = new Date(bValue)?.getTime();
      }

      // Handle string sorting
      if (typeof aValue === 'string') {
        aValue = aValue?.toLowerCase();
        bValue = bValue?.toLowerCase();
      }

      if (aValue < bValue) return sortConfig?.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return sortConfig?.direction === 'asc' ? 1 : -1;
      return 0;
    });

    setFilteredViolations(filtered);
  };

  const handleSort = (key) => {
    setSortConfig(prev => ({
      key,
      direction: prev?.key === key && prev?.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleDelete = async (violationId) => {
    if (window.confirm('Are you sure you want to delete this violation record?')) {
      try {
        const success = await violationRecordsService?.delete(violationId);
        if (success) {
          setViolations(prev => prev?.filter(v => v?.id !== violationId));
        }
      } catch (error) {
        console.error('Error deleting violation:', error);
      }
    }
  };

  const handleExport = () => {
    const csvContent = [
      ['Employee Name', 'Employee ID', 'Department', 'Violation Type', 'Category', 'Points', 'Date', 'Description'],
      ...filteredViolations?.map(v => [
        v?.employeeName,
        v?.employeeIdDisplay,
        v?.department,
        v?.type,
        v?.category,
        v?.points,
        v?.date,
        v?.description || ''
      ])
    ]?.map(row => row?.join(','))?.join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL?.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `violations_export_${new Date()?.toISOString()?.split('T')?.[0]}.csv`;
    link?.click();
    window.URL?.revokeObjectURL(url);
  };

  const uniqueDepartments = [...new Set(employees?.map(e => e?.department)?.filter(Boolean))];
  const uniqueCategories = ['Attendance', 'Conduct', 'Performance', 'Administrative'];

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="pt-16">
        <div className="px-6 py-6">
          <Breadcrumb items={breadcrumbItems} />
          
          {loading ? (
            <div className="flex items-center justify-center py-24">
              <div className="text-center">
                <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-lg text-muted-foreground">Loading violations...</p>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center justify-between mb-6 mt-4">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">All Violations</h1>
                  <p className="text-muted-foreground">
                    Showing {filteredViolations?.length} of {violations?.length} violation records
                  </p>
                </div>
              </div>

              <FilterPanel
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                selectedCategory={selectedCategory}
                onCategoryChange={setSelectedCategory}
                selectedDepartment={selectedDepartment}
                onDepartmentChange={setSelectedDepartment}
                dateRange={dateRange}
                onDateRangeChange={setDateRange}
                pointRange={pointRange}
                onPointRangeChange={setPointRange}
                departments={uniqueDepartments}
                categories={uniqueCategories}
                onExport={handleExport}
                totalCount={filteredViolations?.length}
              />

              <ViolationsTable
                violations={filteredViolations}
                onSort={handleSort}
                sortConfig={sortConfig}
                onDelete={handleDelete}
              />
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default AllViolations;